function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { HttpClient } from 'api/http-client';
import { constructFormData } from './construct-form-data';
import { AttachmentType } from './types';
export var FileUploadStatus = /*#__PURE__*/ function(FileUploadStatus) {
    FileUploadStatus["Done"] = "done";
    FileUploadStatus["Error"] = "error";
    FileUploadStatus["Success"] = "success";
    FileUploadStatus["Uploading"] = "uploading";
    FileUploadStatus["Removed"] = "removed";
    return FileUploadStatus;
}({});
export const MAX_FILE_SIZE = 31457280; // 30 MB;
// Presigned S3 attachment URLs expire after 5 minutes. Cache TTL is set to 4 minutes
// so URLs are refreshed 1 minute before they become invalid.
export const ATTACHMENT_URL_CACHE_TTL_MS = 4 * 60 * 1000;
export class AttachmentsHttpClient extends HttpClient {
    async postRequest(formData) {
        return (await this.post('/attachments', formData)).attachment;
    }
    downloadAttachment(attachmentId) {
        return this.getResponse(`attachments/${attachmentId}/download`, {
            responseType: 'blob'
        });
    }
    async deleteAttachment(attachmentID) {
        return this.delete(`attachments/${attachmentID}/`);
    }
    async renameAttachment(attachmentId, fileName) {
        return this.put('attachments/rename', {
            attachmentId,
            newFileName: fileName
        });
    }
    async attachTaskFiles(file, data) {
        const formData = constructFormData(file, {
            attachment: {
                projectId: data.projectId
            },
            associatedEntity: {
                sourceType: AttachmentType.Task,
                sourceId: data.taskId
            }
        });
        return this.postRequest(formData);
    }
    async attachTemplateFile(file, reqBody) {
        const formData = constructFormData(file, reqBody !== null && reqBody !== void 0 ? reqBody : {});
        return this.postRequest(formData);
    }
    async attachStatusUpdateFiles(file, data) {
        const formData = constructFormData(file, {
            attachment: {
                projectId: data.projectId
            },
            associatedEntity: {
                sourceType: AttachmentType.StatusUpdate,
                sourceId: data.statusUpdateId
            }
        });
        return this.postRequest(formData);
    }
    async attachFiles(file, data) {
        const formData = constructFormData(file, {
            attachment: data
        });
        return (await this.post('attachments', formData)).attachment;
    }
    deleteFile(accountId, projectId, attachmentId) {
        return this.delete(`/account/${accountId}/project/${projectId}/attachment/${attachmentId}`);
    }
    async getAttachment(attachmentId) {
        return (await this.get(`attachments/${attachmentId}`)).attachment;
    }
    getDownloadLinkForAttachmentsInTask(projectId, taskId) {
        return `${this.BASEURL}/projects/${projectId}/tasks/${taskId}/attachments/download`;
    }
    downloadAttachmentsInTask(projectId, taskId) {
        return this.get(`projects/${projectId}/tasks/${taskId}/attachments/download`, {
            responseType: 'blob'
        });
    }
    updateAttachmentMetadata(attachmentId, metadata = {}) {
        return this.put(`attachments/${attachmentId}`, {
            metadata
        });
    }
    addComment(attachmentId, { commentMeta: { annotations }, commentGuid, content, isPrivate }) {
        return this.post(`/attachments/${attachmentId}/discussions`, {
            comment: {
                commentMeta: {
                    annotations
                },
                commentGuid,
                content,
                private: isPrivate
            }
        });
    }
    getDiscussions(attachmentId) {
        return this.get(`/attachments/${attachmentId}/discussions`);
    }
    updateComment(attachmentId, commentId, body) {
        return this.put(`/attachments/${attachmentId}/comments/${commentId}`, {
            comment: body
        });
    }
    addReply(attachmentId, threadId, { content, isPrivate }) {
        return this.post(`/attachments/${attachmentId}/discussions/${threadId}/comments`, {
            comment: {
                content,
                private: isPrivate
            }
        });
    }
    resolveDocumentThread(attachmentId, commentThreadId, resolved) {
        return this.put(`/attachments/${attachmentId}/discussions/${commentThreadId}`, {
            resolved
        });
    }
    async createAttachmentVersion(parentAttachmentId, file, data) {
        var _data_sourceId;
        const formData = constructFormData(file, {
            attachment: {
                projectId: data.projectId
            },
            associatedEntity: {
                sourceType: data.sourceType,
                sourceId: (_data_sourceId = data.sourceId) !== null && _data_sourceId !== void 0 ? _data_sourceId : data.projectId
            }
        });
        return (await this.post(`/attachments/${parentAttachmentId}/version`, formData)).attachment;
    }
    async getAttachmentVersion(attachmentVersionId) {
        return this.get(`/attachments/version/${attachmentVersionId}`);
    }
    async getAllAttachmentVersions(attachmentId) {
        return this.get(`/attachments/${attachmentId}/version`);
    }
    async markAsCurrentVersion(attachmentId, versionMappingId) {
        return this.put(`/attachments/${attachmentId}/version/${versionMappingId}/current`, {});
    }
    updateLastSeen(id) {
        return this.post(`/attachments/${id}/last-seen`, {});
    }
    uploadAttachmentInCKEditor(file) {
        const formData = new FormData();
        formData.append('upload', file);
        return this.post('/attachments/cke-upload', formData);
    }
    constructor(...args){
        super(...args), _define_property(this, "deleteCommentThread", (attachmentId, commentThreadId)=>{
            return this.delete(`/attachments/${attachmentId}/discussions/${commentThreadId}`);
        }), _define_property(this, "deleteComment", (attachmentId, commentId)=>{
            return this.delete(`/attachments/${attachmentId}/comments/${commentId}`);
        }), _define_property(this, "deleteAttachmentVersion", (attachmentVersionId)=>{
            return this.delete(`/attachments/${attachmentVersionId}/version`);
        });
    }
}
export const getUpdatedFileList = (file, fileList, attachment, status)=>{
    const updatedFile = {
        ...file,
        attachment,
        status,
        thumbUrl: attachment === null || attachment === void 0 ? void 0 : attachment.location,
        url: attachment === null || attachment === void 0 ? void 0 : attachment.location
    };
    return fileList.map((f)=>f.uid === updatedFile.uid ? updatedFile : f);
};
export const AttachmentsAPI = new AttachmentsHttpClient();
